
import { useState, useEffect, useRef } from 'react';

const treatmentSteps = [
  {
    icon: 'ri-search-line',
    title: 'İlk Teşhis',
    description: 'Detaylı ağız ve diş analizi ile başlar. Ağız içi tarama, röntgen çekimi ve ağız içi fotoğraflanma işlemleri gerçekleştirilir.',
    image: 'https://public.readdy.ai/ai/img_res/3625b33eb588e3a97c399c78a14c403e.jpg'
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Tasarım Planlanması',
    description: 'İlk muayenede elde edilen veriler doğrultusunda, dijital ortamda dişleriniz için size özel bir tedavi planı tasarlanır.',
    image: 'https://readdy.ai/api/search-image?query=Digital%20dental%20treatment%20planning%20on%20computer%20screen%2C%203D%20dental%20modeling%20software%2C%20orthodontic%20treatment%20design%2C%20modern%20dental%20technology%2C%20professional%20workspace%2C%20clean%20white%20background&width=400&height=300&seq=treatment2&orientation=landscape'
  },
  {
    icon: 'ri-checkbox-circle-line',
    title: 'Onay Süreci',
    description: 'Tedavi süresi, beklenen sonuçlar ve işlem detayları sizinle paylaşılır. Planı onayladığınızda üretim süreci başlar.',
    image: 'https://readdy.ai/api/search-image?query=Patient%20consultation%20with%20dentist%20reviewing%20treatment%20plan%2C%20dental%20office%20meeting%2C%20professional%20discussion%2C%20treatment%20approval%20process%2C%20modern%20dental%20clinic%2C%20clean%20white%20environment&width=400&height=300&seq=treatment3&orientation=landscape'
  },
  {
    icon: 'ri-play-circle-line',
    title: 'Tedavi Başlangıcı',
    description: 'Şeffaf plaklar hazır olduğunda tedaviniz başlar. İlk plağınızı takma ve kullanım talimatları detaylı olarak anlatılır.',
    image: 'https://readdy.ai/api/search-image?query=Patient%20receiving%20first%20clear%20aligners%20from%20dentist%2C%20orthodontic%20treatment%20beginning%2C%20professional%20dental%20care%2C%20modern%20clinic%20setting%2C%20patient%20education%2C%20clean%20white%20background&width=400&height=300&seq=treatment4&orientation=landscape'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Ara Kontrol',
    description: 'Düzenli kontrollerle tedavinin ilerleyişi değerlendirilir. Dişlerinizin pozisyonu kontrol edilir ve sonraki plağa geçiş planlanır.',
    image: 'https://readdy.ai/api/search-image?query=Regular%20dental%20checkup%20appointment%2C%20dentist%20examining%20patient%20progress%2C%20dental%20monitoring%2C%20professional%20healthcare%20follow-up%2C%20clean%20modern%20dental%20office%2C%20bright%20lighting&width=400&height=300&seq=treatment5&orientation=landscape'
  },
  {
    icon: 'ri-trophy-line',
    title: 'Tedavi Bitişi',
    description: 'Tedavi tamamlandığında, sonuçların kalıcılığı için pekiştirme plağı veya retainer teli kullanımı önerilir.',
    image: 'https://readdy.ai/api/search-image?query=Happy%20patient%20with%20perfect%20smile%20after%20orthodontic%20treatment%2C%20successful%20dental%20treatment%20result%2C%20beautiful%20white%20teeth%2C%20confident%20smile%2C%20dental%20clinic%20celebration%2C%20bright%20clean%20background&width=400&height=300&seq=treatment6&orientation=landscape'
  }
];

export default function TreatmentProcessSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Tedavi <span className="text-[#D4AF37]">Süreci</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Şeffaf diş teli tedavisi, etkili ve size özel bir planlama süreci ile ilerler. İşte adım adım tedavi aşamaları:
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] mx-auto rounded-full mt-6"></div>
        </div>
        
        {/* İlk 3 Kart */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {treatmentSteps.slice(0, 3).map((step, index) => (
            <div 
              key={index} 
              className={`bg-white border border-gray-200 rounded-2xl shadow-lg p-6 group hover:shadow-xl hover:scale-105 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="relative overflow-hidden rounded-xl mb-6">
                <img 
                  src={step.image}
                  alt={step.title}
                  className="w-full h-48 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-full flex items-center justify-center mr-4">
                  <i className={`${step.icon} text-xl text-white`}></i>
                </div>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-[#D4AF37] mr-3">0{index + 1}</span>
                  <h3 className="text-xl font-bold text-black">{step.title}</h3>
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
        
        {/* İkinci 3 Kart */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {treatmentSteps.slice(3, 6).map((step, index) => (
            <div 
              key={index + 3} 
              className={`bg-white border border-gray-200 rounded-2xl shadow-lg p-6 group hover:shadow-xl hover:scale-105 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${(index + 3) * 200}ms` }}
            >
              <div className="relative overflow-hidden rounded-xl mb-6">
                <img 
                  src={step.image}
                  alt={step.title}
                  className="w-full h-48 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-full flex items-center justify-center mr-4">
                  <i className={`${step.icon} text-xl text-white`}></i>
                </div>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-[#D4AF37] mr-3">0{index + 4}</span>
                  <h3 className="text-xl font-bold text-black">{step.title}</h3>
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
        
        <div className={`text-center mt-16 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#B8941F]/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-black mb-4">Tedavi Süreniz Hakkında</h3>
            <p className="text-gray-600 text-lg">
              Tedavi süresi kişiden kişiye değişiklik gösterir ve ortalama 6-18 ay arasındadır. 
              Kesin süre ve detaylar için diş hekiminizle görüşmenizi öneririz.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
